import { useInfiniteQuery } from '@tanstack/react-query';

import {
  CommentDTO,
  EngagementLinkPreviewDto,
  EngagementUpdateDTO,
  FeedEngagementUpdatesResponseDto,
  FocusAreaAllResponsesDto,
  FocusAreaDueResponseCountDto,
  IUpdateEngagementFlagsDto,
  MemberUpdateActivityDto,
  ReviewPendingUpdatesRequest,
  UpdateTargetDto
} from '@/features/engagement/engagement.types';
import { get } from '@/helpers/axiosHelper';
import { useDeleteData, useFetchData, usePostData } from '@/helpers/hooks';
import { DropdownOption } from '@/types/generalTypes';

// Commands
export const useUpdateEngagementFlags = () =>
  usePostData<any, any, IUpdateEngagementFlagsDto, unknown>({
    url: '/Engagement/UpdateEngagementFlags'
  });

export const useUpdateEngagePost = () =>
  usePostData<any, any, FormData, unknown>({
    url: '/Engagement/UpdateEngagePost'
  });

export const useDeleteEngagePost = () => useDeleteData<any, any, unknown>('/Engagement/DeleteEngagePost');

export const useUpdateSignalResponse = () =>
  usePostData<unknown, unknown, FormData, unknown>({
    url: '/Engagement/UpdateSignalResponse',
    options: {
      meta: { disableAutoInvalidate: true }
    }
  });

export const useReviewPendingUpdates = () =>
  usePostData<any, unknown, ReviewPendingUpdatesRequest>({
    url: '/Engagement/ReviewPendingUpdates',
    options: {
      meta: {
        disableAutoInvalidate: true
      }
    }
  });

// Queries
export const useGetEngagementFocusAreas = () =>
  useFetchData<DropdownOption[]>(['engagementFocusAreas'], '/Engagement/GetEngagementFocusAreas', {
    refetchOnWindowFocus: false
  });

export const useGetUpdateTargets = (enabled = true) =>
  useFetchData<UpdateTargetDto[]>(['engagementUpdateTargets'], '/Engagement/GetUpdateTargets', {
    enabled,
    refetchOnWindowFocus: false
  });

export const useGetFocusAreaDueResponseCounts = () =>
  useFetchData<FocusAreaDueResponseCountDto[]>(
    ['focusAreaDueResponseCounts'],
    '/Engagement/GetFocusAreaDueResponseCounts',
    {
      refetchOnWindowFocus: false
    }
  );

export const useGetMemberUpdateActivity = (days: 28 | 90 | 365, fromDate?: string, toDate?: string, enabled = true) => {
  const customQuery = fromDate && toDate ? `&fromDate=${fromDate}&toDate=${toDate}` : '';
  return useFetchData<MemberUpdateActivityDto>(
    ['memberUpdateActivity', days, fromDate, toDate],
    `/Engagement/GetMemberUpdateActivity?days=${days}${customQuery}`,
    {
      enabled,
      refetchOnWindowFocus: false
    }
  );
};

export const useGetFocusAreaAllResponses = (focusAreaId: string | null, shouldFetch: boolean) =>
  useFetchData<FocusAreaAllResponsesDto>(
    ['focusAreaAllResponses', focusAreaId],
    `/Engagement/GetFocusAreaAllResponses?focusAreaId=${focusAreaId}`,
    {
      enabled: shouldFetch && !!focusAreaId,
      refetchOnWindowFocus: false
    }
  );

// export const useGetFeedEngagementUpdates = () =>
//   useFetchData<FeedEngagementUpdatesResponseDto>(
//     ['feedEngagementUpdates'],
//     '/Engagement/GetFeedEngagementUpdates?pageSize=20',
//     {
//       refetchOnWindowFocus: false
//     }
//   );

export const useGetFeedEngagementUpdatesInfinite = (pageSize = 20) =>
  useInfiniteQuery({
    queryKey: ['feedEngagementUpdates', 'infinite', pageSize],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const cursorQuery = pageParam ? `&cursorCreatedTimeUtc=${encodeURIComponent(pageParam)}` : '';
      const response = await get<FeedEngagementUpdatesResponseDto>(
        `/Engagement/GetFeedEngagementUpdates?pageSize=${pageSize}${cursorQuery}`,
        { timeout: 30000 }
      );
      return response.data;
    },
    getNextPageParam: lastPage => (lastPage.hasMore ? lastPage.nextCursorCreatedTimeUtc : undefined),
    retry: false,
    refetchOnWindowFocus: false
  });

export const useGetSocialComments = (itemId: string, shoudlShowComments: boolean) =>
  useFetchData<CommentDTO[]>(['socialComments', itemId], `/Engagement/GetSocialComments/${itemId}/comments`, {
    enabled: shoudlShowComments,
    refetchOnWindowFocus: false
  });

export const useGetPendingPublishItems = (enabled = true) =>
  useFetchData<EngagementUpdateDTO[]>(['pendingPublishItems'], '/Engagement/GetPendingPublishItems', {
    enabled,
    refetchOnWindowFocus: false,
    retry: false
  });

export const useGetEngagementUpdateById = (updateId: string, type?: string) =>
  useFetchData<EngagementUpdateDTO>(
    ['engageUpdate', updateId, type],
    `/Engagement/GetEngagementUpdateById/${updateId}${type ? `?type=${encodeURIComponent(type)}` : ''}`,
    {
      refetchOnWindowFocus: false
    }
  );

export const useGetQuestionsByFocusArea = (
  selectedFocusAreaId: any,
  debouncedSearchTerm: string,
  shouldFetchQuestions: boolean
) =>
  useFetchData<DropdownOption[]>(
    ['questions', selectedFocusAreaId, debouncedSearchTerm],
    `/Engagement/GetQuestionsByFocusArea?focusAreaId=${selectedFocusAreaId}`,
    {
      enabled: shouldFetchQuestions,
      refetchOnWindowFocus: false
    }
  );

export const useGetBusinessUnitDropdownOptions = () =>
  useFetchData<DropdownOption[]>(['businessUnitDropdownOptions'], '/BusinessUnit/GetBusinessUnitDropdownOptions', {
    refetchOnWindowFocus: false
  });

export const useGetEngagementLinkPreview = (url: string, shouldFetch: boolean) =>
  useFetchData<EngagementLinkPreviewDto | null>(
    ['engagementUrlLinkPreview', url],
    `/Engagement/GetLinkPreview?url=${encodeURIComponent(url)}`,
    {
      enabled: shouldFetch,
      staleTime: 60 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false
    }
  );

export const useGetSignalWithSingleResponse = (responseId?: string, signalId?: string, isEngagement?: boolean) => {
  const query = new URLSearchParams();

  if (responseId) {
    query.set('responseId', responseId);
  }
  if (signalId) {
    query.set('signalId', signalId);
  }
  if (isEngagement) {
    query.set('isEngagement', String(isEngagement));
  }

  return useFetchData<EngagementUpdateDTO>(
    ['signalWithSingleResponse', responseId, signalId, isEngagement],
    `/Engagement/GetSignalWithSingleResponse?${query.toString()}`,
    {
      enabled: !!(responseId || signalId),
      refetchOnWindowFocus: false,
      retry: false
    }
  );
};
